import { useState, useMemo, useCallback } from "react";
import { isOver62, isPresent } from "../components/inscriptionHelpers";

const useSessionFilters = (
  inscriptions,
  { onFilterChange, directorDecisions } = {},
) => {
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterDetection, setFilterDetection] = useState("");
  const [filterPresence, setFilterPresence] = useState("");
  const [filterDecision, setFilterDecision] = useState("");
  const [filterAlert, setFilterAlert] = useState("");
  const [filterDirectorDecision, setFilterDirectorDecision] = useState("");
  const [sortBy, setSortBy] = useState("index");
  const [sortDir, setSortDir] = useState("asc");

  const wrap = useCallback(
    (setter) => (value) => {
      setter(value);
      if (onFilterChange) onFilterChange();
    },
    [onFilterChange],
  );

  const filteredInscriptions = useMemo(() => {
    let result = inscriptions.filter((ins) => {
      if (search) {
        const s = search.toLowerCase();
        const name = (ins.userRecipient?.fullname || "").toLowerCase();
        if (!name.includes(s)) return false;
      }
      if (filterType && ins.type !== filterType) return false;
      if (filterDetection && ins.detectionType !== filterDetection)
        return false;
      if (filterPresence) {
        if (filterPresence === "present") {
          if (!isPresent(ins.presenceId)) return false;
        } else if (filterPresence === "absent") {
          if (ins.presenceId !== "ABSENT") return false;
        } else if (filterPresence === "absent_observations") {
          if (ins.presenceId !== "ABSENT_OBSERVATIONS_ECRITES") return false;
        } else if (filterPresence === "non_convoque") {
          if (ins.presenceId !== "NON_CONVOQUE") return false;
        } else if (filterPresence === "none") {
          if (ins.presenceId) return false;
        }
      }
      if (filterDecision === "with" && !ins.decisionId) return false;
      if (filterDecision === "without" && ins.decisionId) return false;
      if (filterDirectorDecision && directorDecisions) {
        const dd = directorDecisions[ins.id];
        if (filterDirectorDecision === "ACCORD" && dd?.decision !== "ACCORD")
          return false;
        if (
          filterDirectorDecision === "A_SURSEOIR" &&
          dd?.decision !== "A_SURSEOIR"
        )
          return false;
        if (filterDirectorDecision === "none" && dd) return false;
      }
      if (filterAlert === "over62" && !isOver62(ins)) return false;
      if (filterAlert === "pregnancy" && !ins.pregnancy) return false;
      return true;
    });

    if (sortBy !== "index") {
      result = [...result].sort((a, b) => {
        let cmp = 0;
        if (sortBy === "name") {
          const stripCivility = (name) =>
            (name || "").replace(/^(Mr|Mme|M\.)\s+/i, "");
          cmp = stripCivility(a.userRecipient?.fullname).localeCompare(
            stripCivility(b.userRecipient?.fullname),
          );
        } else if (sortBy === "type") {
          cmp = (a.type || "").localeCompare(b.type || "");
        } else if (sortBy === "detection") {
          cmp = (a.detectionType || "").localeCompare(b.detectionType || "");
        } else if (sortBy === "decision") {
          cmp = (a.decision ? 1 : 0) - (b.decision ? 1 : 0);
        }
        return sortDir === "asc" ? cmp : -cmp;
      });
    } else if (sortDir === "desc") {
      result = [...result].reverse();
    }

    return result;
  }, [
    inscriptions,
    search,
    filterType,
    filterDetection,
    filterPresence,
    filterDecision,
    filterDirectorDecision,
    filterAlert,
    sortBy,
    sortDir,
    directorDecisions,
  ]);

  return {
    filteredInscriptions,
    filterProps: {
      search,
      setSearch: wrap(setSearch),
      filterType,
      setFilterType: wrap(setFilterType),
      filterDetection,
      setFilterDetection: wrap(setFilterDetection),
      filterPresence,
      setFilterPresence: wrap(setFilterPresence),
      filterDecision,
      setFilterDecision: wrap(setFilterDecision),
      filterDirectorDecision,
      setFilterDirectorDecision: wrap(setFilterDirectorDecision),
      filterAlert,
      setFilterAlert: wrap(setFilterAlert),
      sortBy,
      setSortBy: wrap(setSortBy),
      sortDir,
      setSortDir: wrap(setSortDir),
      totalCount: inscriptions.length,
      filteredCount: filteredInscriptions.length,
    },
  };
};

export default useSessionFilters;
